import { Box, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ENTRY_LINK as ACCOUNT_ENTRY_LINK } from 'account';
import { Context as AppConfigContext } from 'application/Configuration';
import { Context as FeatureToggleContext } from 'application/FeatureToggle';
import { Context as AuthenticationContext } from 'authentication/Context';
import { Context as AuthorizationContext } from 'authorization/Context';
import clsx from 'clsx';
import {
  RELEASE_DIRECT_MESSAGE_INBOX_FLAG,
  RELEASE_WOUNDSCRIBE_ENABLED_FLAG,
} from 'common/constants';
import LDProviderWrapper, { useLDClientCache } from 'common/LDProviderWrapper';
import ResponsiveHidden from 'common/ResponsiveHidden';
import { useLDFlagValue } from 'common/useLDFlagValue';
import { checkRoutePermission } from 'common/utils';
import { RECALCULATE_SEQUENCE } from 'document-management/constants';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Drawer from 'styleguide/Drawer';
import { getEmail } from 'styleguide/form/Telecom/helpers';
import LockedScreen from 'styleguide/LockedScreen';
import Alert from 'styleguide/layout/Alert/Alert';
import { NAVIGATION_DRAWER_WIDTH } from 'utility/constants';
import { debounce, detectDevice } from 'utility/helpers';
import ManualAudioNotifications from 'woundscribe/components/ManualAudioNotifications/ManualAudioNotifications';
import { useIsWoundScribeSessionActive } from 'woundscribe/core/recordingStore';
import { CONFIG_MULTI_OPTIONS_VISIBILITY_STATUS } from './constants';
import { Bar } from './index';
import Navigation from './Navigation';

// The trick to viewport units on mobile
function getDynamicViewportHeight() {
  const calculatedViewportHeight = 'calc(var(--vh, 1vh) * 100)';
  const isJsCssVarSupported = !!document?.documentElement?.style?.setProperty;
  const isCssVarSupported =
    CSS && CSS.supports && CSS.supports('height', calculatedViewportHeight);

  if (isCssVarSupported && isJsCssVarSupported) {
    return calculatedViewportHeight;
  }

  return '100vh';
}

function setViewportHeight() {
  if (document?.documentElement?.style?.setProperty) {
    setTimeout(() => {
      document.documentElement.style.setProperty(
        '--vh',
        `${window.innerHeight * 0.01}px`
      );
    }, 200);
  }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerOpen: {
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: `calc(100% - ${NAVIGATION_DRAWER_WIDTH})`,
      },
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    drawerClose: {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: '100%',
    },
    viewport: {
      height: getDynamicViewportHeight(),
      [theme.breakpoints.up('lg')]: {
        height: `100vh`,
      },
    },
    woundScribeFooterBarSpacer: {
      paddingBottom: 48,
    },
  })
);

export type Props = {
  navigationItems: any[];
  children?: React.ReactNode;
};

const PrivateLayout: React.FC<Props> = ({ children, navigationItems }) => {
  const location = useLocation();
  const path = location.pathname;

  const classes = useStyles();
  const { user, isAuthorized } = useContext(AuthorizationContext);
  const {
    signOut,
    lockedScreen,
    screenLock,
    screenUnlock,
    sequencedDestinationsPrivate,
  } = useContext(AuthenticationContext);
  const { IdleLogoutDelay, DeploymentEnvironment } =
    useContext(AppConfigContext);

  const IdleScreenLockDelay = window?._env_?.IDLE_LOCK_SCREEN_DELAY || 600000; // need to implement in config api in order to get this from config context
  const featureToggles = useContext(FeatureToggleContext);
  const directMessageInboxEnabled = useLDFlagValue(
    RELEASE_DIRECT_MESSAGE_INBOX_FLAG
  );
  const isWoundScribeEnabled = useLDFlagValue(RELEASE_WOUNDSCRIBE_ENABLED_FLAG);
  const isWoundScribeSessionActive = useIsWoundScribeSessionActive();
  // Mirrors WoundScribeFooterBar's visibility: shown on every page once a
  // session is active, otherwise only on the encounter's own page.
  const showWoundScribeFooterBar =
    isWoundScribeEnabled && isWoundScribeSessionActive;

  const mergedFeatureToggles = useMemo(() => {
    const merged = { ...featureToggles };

    if (directMessageInboxEnabled === undefined) {
      delete merged[RELEASE_DIRECT_MESSAGE_INBOX_FLAG];
    } else {
      merged[RELEASE_DIRECT_MESSAGE_INBOX_FLAG] =
        directMessageInboxEnabled === true ? 'true' : 'false';
    }

    return merged;
  }, [featureToggles, directMessageInboxEnabled]);
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
  const isMobileOS = detectDevice() === 'tablet';
  const [open, setOpen] = React.useState(true);
  const lastActivityRef = useRef(Date.now());
  const { setLDClinicianEmail, ldClinicianEmail } = useLDClientCache();
  const clinicianEmail = useMemo(
    () => getEmail(user?.telecom),
    [user?.telecom]
  );

  const [showResequenceAlert, setShowResequenceAlert] = React.useState(false);
  const toggleOpen = useCallback(() => {
    if (
      sequencedDestinationsPrivate &&
      sequencedDestinationsPrivate?.length > 0
    ) {
      setShowResequenceAlert(true);
    } else {
      setOpen(!open);
    }
  }, [sequencedDestinationsPrivate, open]);

  useEffect(() => {
    isMobile ? setOpen(false) : setOpen(true);
  }, [isMobile]);

  // Keeps the fixed WoundScribe footer bar (mounted at the app root, outside
  // this layout) from sitting under the permanent sidebar, which MUI renders
  // with position: fixed regardless of variant="permanent".
  useEffect(() => {
    const sidebarWidth = !isMobile && open ? NAVIGATION_DRAWER_WIDTH : '0px';
    document.documentElement.style.setProperty(
      '--woundscribe-footer-left',
      sidebarWidth
    );
  }, [isMobile, open]);

  const handleDragAndDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const { function: activateIdleLogout, cancel: deactivateIdleLogout } =
    useMemo(
      () =>
        debounce(() => {
          signOut(true);
        }, IdleLogoutDelay || 5400000),
      [IdleLogoutDelay, signOut]
    );

  const { function: activateIdleScreenLock, cancel: deactivateIdleScreenLock } =
    useMemo(
      () =>
        debounce(() => {
          screenLock();
        }, IdleScreenLockDelay || 600000),
      [IdleScreenLockDelay, screenLock]
    );

  const checkTimestamps = useCallback(() => {
    const dateNow = Date.now();
    const timeDiference = dateNow - lastActivityRef.current;
    if (IdleLogoutDelay && timeDiference >= IdleLogoutDelay) {
      signOut(true);
    }
    if (IdleScreenLockDelay && timeDiference >= Number(IdleScreenLockDelay)) {
      screenLock();
    }
    lastActivityRef.current = dateNow;
  }, [IdleLogoutDelay, IdleScreenLockDelay, screenLock, signOut]);
  const onActivity = useCallback(
    (_) => {
      checkTimestamps();
      activateIdleLogout();
      activateIdleScreenLock();
    },
    [checkTimestamps, activateIdleLogout, activateIdleScreenLock]
  );

  useEffect(() => {
    activateIdleLogout();
    activateIdleScreenLock();
    return () => {
      deactivateIdleLogout();
      deactivateIdleScreenLock();
    };
  }, [
    activateIdleScreenLock,
    deactivateIdleScreenLock,
    deactivateIdleLogout,
    activateIdleLogout,
  ]);

  const onVisibilityChange = useCallback(
    (_) => {
      checkTimestamps();
    },
    [checkTimestamps]
  );

  useEffect(() => {
    if (!isMobileOS) {
      return;
    }
    window.addEventListener('visibilitychange', onVisibilityChange, false);
    window.addEventListener('touchstart', onActivity, false);
    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange, false);
      window.removeEventListener('touchstart', onActivity, false);
    };
  }, [onVisibilityChange, isMobileOS, onActivity]);

  // The trick to viewport units on mobile
  useEffect(() => {
    setViewportHeight();
    window.addEventListener('orientationchange', setViewportHeight);
    window.addEventListener('resize', setViewportHeight);

    return () => {
      window.removeEventListener('orientationchange', setViewportHeight);
      window.removeEventListener('resize', setViewportHeight);
    };
  }, []);

  //close menu on locked in mobile
  useEffect(() => {
    if (isMobile && lockedScreen) {
      setOpen(false);
    }
  }, [isMobile, lockedScreen]);

  useEffect(() => {
    if (!clinicianEmail || ldClinicianEmail === clinicianEmail) {
      return;
    }
    setLDClinicianEmail(clinicianEmail ?? '');
  }, [clinicianEmail, ldClinicianEmail, setLDClinicianEmail]);

  const multiOptionsVisibilityStatus = useCallback(
    (value) => {
      const valueStatus =
        CONFIG_MULTI_OPTIONS_VISIBILITY_STATUS?.[value] || null;
      return valueStatus ? featureToggles?.[valueStatus] : null;
    },
    [featureToggles]
  );

  const filterCheck = (option) => {
    const checkAllowedConfigList = multiOptionsVisibilityStatus(option?.path);
    let menuItemEnabled = true;
    if (checkAllowedConfigList !== null) {
      menuItemEnabled = Boolean(checkAllowedConfigList === 'true');
    }
    if (option?.permission) {
      const authValue = option?.displayLink
        ? checkRoutePermission(option?.permission, isAuthorized) &&
          option?.displayLink(user, DeploymentEnvironment, mergedFeatureToggles)
        : checkRoutePermission(option?.permission, isAuthorized);
      return authValue && menuItemEnabled;
    }
    return true;
  };

  const filterPermittedNavigationItems = (navigationItems): any[] => {
    const filteredPermittedNavigationItems: any[] = [];
    navigationItems.forEach((navigationItem) => {
      const option = { ...navigationItem };
      if (filterCheck(option)) {
        if (!!option.children)
          option.children = filterPermittedNavigationItems(option.children);
        filteredPermittedNavigationItems.push(option);
      }
    });
    return filteredPermittedNavigationItems;
  };

  const permittedNavigationItems =
    filterPermittedNavigationItems(navigationItems);

  const flatenItems = (items: any, flatenedItems: any[]): any[] => {
    for (const item of items) {
      if (!!item.children) flatenItems(item.children, flatenedItems);
      else flatenedItems.push(item);
    }
    return flatenedItems;
  };

  const flattenedPermittedNavigationItems = flatenItems(
    permittedNavigationItems,
    []
  );

  const removeAccountEntryLink = (items) => {
    const filteredItems: any[] = [];
    for (const currentItem of items) {
      const item = { ...currentItem };
      if (!!item.children)
        item.children = removeAccountEntryLink(item.children);
      if (item.path !== ACCOUNT_ENTRY_LINK.path) filteredItems.push(item);
    }
    return filteredItems;
  };

  const items = removeAccountEntryLink(permittedNavigationItems);

  const isCurrentPathAllowed = flattenedPermittedNavigationItems.find((item) =>
    path.startsWith(item.path)
  );

  if (!isCurrentPathAllowed && items.length) {
    let pathname;
    const defaultRoute = flattenedPermittedNavigationItems.find(
      (route) =>
        route?.isDefaultLink &&
        route?.isDefaultLink(user, DeploymentEnvironment)
    );

    if (defaultRoute) {
      pathname = defaultRoute?.path;
    } else {
      pathname = flattenedPermittedNavigationItems?.[0]?.path;
    }

    return <Navigate to={pathname} replace />;
  }

  return (
    <>
      <LDProviderWrapper clinicianEmail={clinicianEmail}>
        {null}
      </LDProviderWrapper>
      <ManualAudioNotifications />
      <Box
        className={classes.viewport}
        display="flex"
        flexDirection="column"
        onDrop={handleDragAndDrop}
        onDragOver={handleDragAndDrop}
        onMouseMove={onActivity}
        onClick={onActivity}
        onKeyPress={onActivity}
      >
        <Box id="locking-wrapper">
          <LockedScreen locked={lockedScreen} onUnlock={() => screenUnlock()} />
        </Box>
        <Box>
          <Bar onToggleMenu={toggleOpen} />
        </Box>
        <ResponsiveHidden mdDown>
          <Box display="flex" flexGrow={1} overflow="hidden auto">
            <Drawer variant="permanent" open={open}>
              <Navigation items={items} />
            </Drawer>
            <Box
              p={1}
              height="auto"
              className={clsx({
                [classes.drawerOpen]: open,
                [classes.drawerClose]: !open,
                [classes.woundScribeFooterBarSpacer]: showWoundScribeFooterBar,
              })}
            >
              {children}
            </Box>
          </Box>
        </ResponsiveHidden>
        <ResponsiveHidden lgUp>
          <Box display="flex" flexGrow={1} overflow="hidden auto">
            <Drawer variant="temporary" open={open} toggleOpen={toggleOpen}>
              <Navigation items={items} toggleOpen={toggleOpen} />
            </Drawer>
            <Box
              height="auto"
              width={1}
              className={clsx({
                [classes.woundScribeFooterBarSpacer]: showWoundScribeFooterBar,
              })}
            >
              {children}
            </Box>
          </Box>
        </ResponsiveHidden>
        {showResequenceAlert && (
          <Alert
            open={showResequenceAlert}
            title=""
            submitButtonText="Okay"
            hideCancelButton
            onCancel={() => setShowResequenceAlert(false)}
            onSubmit={() => setShowResequenceAlert(false)}
          >
            {RECALCULATE_SEQUENCE}
          </Alert>
        )}
      </Box>
    </>
  );
};

export default PrivateLayout;
